'use client';

import { useState, type ReactNode } from 'react';
import { ChevronRight, Download, Share, Smartphone } from 'lucide-react';
import { usePwaInstall } from './usePwaInstall';

type InstallAppSectionVariant = 'button' | 'menu' | 'card';

interface InstallAppSectionProps {
  variant?: InstallAppSectionVariant;
  className?: string;
}

export function InstallAppSection({ variant = 'button', className = '' }: InstallAppSectionProps) {
  const { isInstalled, isIOS, isMobile, isInstallable, canShowInstall, promptInstall } = usePwaInstall();
  const [showIosSteps, setShowIosSteps] = useState(false);

  if (!canShowInstall) {
    return null;
  }

  const handlePrimaryAction = async () => {
    if (isInstallable) {
      await promptInstall();
      return;
    }
    setShowIosSteps((open) => !open);
  };

  const subtitle = isInstalled
    ? ''
    : isIOS
      ? 'Add Coordal Maps to your Home Screen'
      : isInstallable
        ? 'Add to screen for faster access and full-screen maps'
        : isMobile
          ? 'Install from your browser menu'
          : 'Available in Chrome or Edge on desktop';

  if (variant === 'menu') {
    return (
      <div className={className}>
        <button
          type="button"
          onClick={handlePrimaryAction}
          className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <Download size={20} className="text-primary-500 dark:text-primary-400" />
          <div className="flex-1 text-left min-w-0">
            <span className="block text-gray-700 dark:text-gray-200 font-medium">Install App</span>
            {subtitle ? (
              <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{subtitle}</span>
            ) : null}
          </div>
          <ChevronRight size={20} className="text-gray-400 shrink-0" />
        </button>
        {showIosSteps ? (
          <InstallSteps mode={isIOS ? 'ios' : isMobile ? 'android' : 'desktop'} />
        ) : null}
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <div className={`rounded-xl border border-primary-200/60 dark:border-primary-800/40 bg-primary-50/50 dark:bg-primary-900/15 p-4 ${className}`}>
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center shrink-0">
            <Smartphone size={20} className="text-primary-600 dark:text-primary-400" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-gray-900 dark:text-white">Install Coordal Maps</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{subtitle}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handlePrimaryAction}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl font-semibold text-sm transition-all shadow-md shadow-primary-500/20"
        >
          <Download size={18} strokeWidth={2.25} />
          {isIOS ? 'How to install' : isInstallable ? 'Install App' : 'Install instructions'}
        </button>
        {showIosSteps ? (
          <InstallSteps mode={isIOS ? 'ios' : isMobile ? 'android' : 'desktop'} className="mt-3" />
        ) : null}
      </div>
    );
  }

  return (
    <div className={className}>
      <button
        type="button"
        onClick={handlePrimaryAction}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl font-semibold text-sm transition-all shadow-lg shadow-primary-500/25"
      >
        <Download size={18} strokeWidth={2.25} />
        <span>{isIOS ? 'Add to Home Screen' : isInstallable ? 'Install App' : 'Install instructions'}</span>
      </button>
      {showIosSteps ? (
        <InstallSteps mode={isIOS ? 'ios' : isMobile ? 'android' : 'desktop'} className="mt-3" />
      ) : null}
    </div>
  );
}

type InstallStepsMode = 'ios' | 'android' | 'desktop';

function InstallSteps({ mode, className = '' }: { mode: InstallStepsMode; className?: string }) {
  if (mode === 'ios') {
    return (
      <div className={`rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3 space-y-2.5 ${className}`}>
        <Step n={1} text={<>Tap <Share size={12} className="inline text-blue-500" /> Share in Safari</>} />
        <Step n={2} text='Tap "Add to Home Screen"' />
        <Step n={3} text='Tap "Add" to confirm' />
      </div>
    );
  }

  return (
    <div className={`rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3 space-y-2.5 ${className}`}>
      {mode === 'desktop' ? (
        <>
          <Step n={1} text="Look for the install icon in the address bar (Chrome/Edge)" />
          <Step n={2} text='Or open the browser menu → "Install Coordal Maps"' />
          <Step n={3} text="Confirm to add the app" />
        </>
      ) : (
        <>
          <Step n={1} text="Open the browser menu (⋮ or ⋯)" />
          <Step n={2} text='Choose "Install app" or "Add to Home screen"' />
          <Step n={3} text="Confirm to add Coordal Maps" />
        </>
      )}
    </div>
  );
}

function Step({ n, text }: { n: number; text: ReactNode }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-600 dark:text-gray-300 shrink-0">
        {n}
      </span>
      <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed pt-0.5">{text}</p>
    </div>
  );
}
